import { ICancellationIntercept } from "@smartrr/shared/entities/CancellationRelationship";
import { typedFrontendVendorApi } from "@vendor-app/utils/typedFrontendVendorApi";
import { checkMediaUrl } from "@vendor-app/utils/validateMediaUrl";
import { create } from "zustand";

interface FormFields {
  headline: string;
  bodyMessage: string;
  mediaUrl: string;
  primaryButtonLabel: string;
  secondaryButtonLabel: string;
}

interface FieldErrors {
  headline: boolean;
  primaryButtonLabel: boolean;
  secondaryButtonLabel: boolean;
}

const emptyFormFields: FormFields = {
  headline: "",
  bodyMessage: "",
  mediaUrl: "",
  primaryButtonLabel: "",
  secondaryButtonLabel: "",
};

const emptyFieldErrors: FieldErrors = {
  headline: false,
  primaryButtonLabel: false,
  secondaryButtonLabel: false,
};

async function getMediaUrlError(mediaUrl: string): Promise<string | undefined> {
  const result = await checkMediaUrl(mediaUrl);
  return result?.error;
}

interface CancellationInterceptStoreProps {
  isEnabled: boolean;
  existingId: string | null;
  headline: string;
  bodyMessage: string;
  mediaUrl: string;
  primaryButtonLabel: string;
  secondaryButtonLabel: string;
  savedFormFields: FormFields;
  fieldErrors: FieldErrors;

  actions: {
    initialize: () => Promise<{ success: boolean; errorMessage?: string }>;
    save: () => Promise<{ success: boolean; errorMessage?: string }>;
    discard: () => void;
    toggleEnabled: (value: boolean) => void;
    setHeadline: (value: string) => void;
    setBodyMessage: (value: string) => void;
    setMediaUrl: (value: string) => void;
    setPrimaryButtonLabel: (value: string) => void;
    setSecondaryButtonLabel: (value: string) => void;
  };
}

const useCancellationInterceptStore = create<CancellationInterceptStoreProps>()((set, get) => ({
  isEnabled: false,
  existingId: null,
  headline: "",
  bodyMessage: "",
  mediaUrl: "",
  primaryButtonLabel: "",
  secondaryButtonLabel: "",
  savedFormFields: emptyFormFields,
  fieldErrors: emptyFieldErrors,
  error: {
    showError: false,
    message: "",
  },

  actions: {
    async initialize() {
      const response = await typedFrontendVendorApi.getReq("/cancellation-intercept");

      if (response.type === "error") {
        if (response.status === 404) {
          return { success: true };
        }
        return { success: false, errorMessage: "Failed to load cancellation intercept settings" };
      }

      const { id, headlineText, bodyText, mediaUrl, primaryButtonLabel, secondaryButtonLabel } = response.body;

      const formFields: FormFields = {
        headline: headlineText,
        bodyMessage: bodyText ?? "",
        mediaUrl: mediaUrl ?? "",
        primaryButtonLabel,
        secondaryButtonLabel,
      };

      set({ existingId: id, ...formFields, savedFormFields: formFields });
      return { success: true };
    },

    async save() {
      const {
        existingId,
        headline: headlineText,
        bodyMessage: bodyText,
        mediaUrl,
        primaryButtonLabel,
        secondaryButtonLabel,
        savedFormFields,
      } = get();

      const hasChanges =
        headlineText !== savedFormFields.headline ||
        bodyText !== savedFormFields.bodyMessage ||
        mediaUrl !== savedFormFields.mediaUrl ||
        primaryButtonLabel !== savedFormFields.primaryButtonLabel ||
        secondaryButtonLabel !== savedFormFields.secondaryButtonLabel;

      if (!hasChanges) {
        return { success: true };
      }

      const fieldErrors: FieldErrors = {
        headline: !headlineText,
        primaryButtonLabel: !primaryButtonLabel,
        secondaryButtonLabel: !secondaryButtonLabel,
      };

      if (Object.values(fieldErrors).some(Boolean)) {
        set({ fieldErrors });
        return { success: false, errorMessage: "Please fill in all required fields before saving" };
      }

      if (mediaUrl) {
        const mediaUrlError = await getMediaUrlError(mediaUrl);
        if (mediaUrlError) {
          return { success: false, errorMessage: mediaUrlError };
        }
      }

      const reqBody = { headlineText, bodyText, mediaUrl, primaryButtonLabel, secondaryButtonLabel };

      try {
        let savedId = existingId;

        if (existingId) {
          const response = await typedFrontendVendorApi.putReq(
            "/cancellation-intercept/:cancellationInterceptId",
            { reqBody, params: { cancellationInterceptId: existingId } }
          );
          if (response.type !== "success") {
            return { success: false, errorMessage: "Failed to save cancellation intercept settings" };
          }
        } else {
          const response = await typedFrontendVendorApi.postReq("/cancellation-intercept", { reqBody });
          if (response.type !== "success") {
            return { success: false, errorMessage: "Failed to save cancellation intercept settings" };
          }
          savedId = (response.body as ICancellationIntercept).id;
        }

        set({
          existingId: savedId,
          savedFormFields: {
            headline: headlineText,
            bodyMessage: bodyText ?? "",
            mediaUrl: mediaUrl ?? "",
            primaryButtonLabel,
            secondaryButtonLabel,
          },
        });
      } catch {
        return { success: false, errorMessage: "Failed to save cancellation intercept settings" };
      }

      return { success: true };
    },

    discard: () => set(state => ({ ...state.savedFormFields, fieldErrors: emptyFieldErrors })),
    toggleEnabled: value => set({ isEnabled: value }),
    setHeadline: value =>
      set(state => ({ headline: value, fieldErrors: { ...state.fieldErrors, headline: false } })),
    setBodyMessage: value => set({ bodyMessage: value }),
    setMediaUrl: value => set({ mediaUrl: value }),
    setPrimaryButtonLabel: value =>
      set(state => ({
        primaryButtonLabel: value,
        fieldErrors: { ...state.fieldErrors, primaryButtonLabel: false },
      })),
    setSecondaryButtonLabel: value =>
      set(state => ({
        secondaryButtonLabel: value,
        fieldErrors: { ...state.fieldErrors, secondaryButtonLabel: false },
      })),
  },
}));

const initialStoreState = useCancellationInterceptStore.getState();

export const CancellationInterceptAccessStore = {
  useActions() {
    return useCancellationInterceptStore(state => state.actions);
  },
  useIsEnabled() {
    return useCancellationInterceptStore(state => state.isEnabled);
  },
  useFormFields() {
    const headline = useCancellationInterceptStore(state => state.headline);
    const bodyMessage = useCancellationInterceptStore(state => state.bodyMessage);
    const mediaUrl = useCancellationInterceptStore(state => state.mediaUrl);
    const primaryButtonLabel = useCancellationInterceptStore(state => state.primaryButtonLabel);
    const secondaryButtonLabel = useCancellationInterceptStore(state => state.secondaryButtonLabel);

    return { headline, bodyMessage, mediaUrl, primaryButtonLabel, secondaryButtonLabel };
  },
  useFieldErrors() {
    return useCancellationInterceptStore(state => state.fieldErrors);
  },
  useHasFormFieldChanges() {
    const headline = useCancellationInterceptStore(state => state.headline);
    const bodyMessage = useCancellationInterceptStore(state => state.bodyMessage);
    const mediaUrl = useCancellationInterceptStore(state => state.mediaUrl);
    const primaryButtonLabel = useCancellationInterceptStore(state => state.primaryButtonLabel);
    const secondaryButtonLabel = useCancellationInterceptStore(state => state.secondaryButtonLabel);
    const saved = useCancellationInterceptStore(state => state.savedFormFields);

    return (
      headline !== saved.headline ||
      bodyMessage !== saved.bodyMessage ||
      mediaUrl !== saved.mediaUrl ||
      primaryButtonLabel !== saved.primaryButtonLabel ||
      secondaryButtonLabel !== saved.secondaryButtonLabel
    );
  },
  testing: {
    store() {
      return useCancellationInterceptStore.getState();
    },
    actions() {
      return {
        actions: useCancellationInterceptStore.getState().actions,
      };
    },
    reset() {
      useCancellationInterceptStore.setState(initialStoreState);
    },
  },
};
